import { useState, useEffect, useCallback } from 'react';
import type { AxiosInstance } from 'axios';
import {
    getLatestReport,
    getReportHistory,
    getAccountHistory,
    getPacks,
    getAccountLabels,
    getAlertRecipients,
} from '../request/request';

export interface FortiFlexReport {
    id: number;
    reportDate: string;
    alertLevel: 'OK' | 'WARNING' | 'CRITICAL';
    pointsTotal: number;
    pointsUsed: number;
    pointsRemaining: number;
    burnRate30d: number;
    burnRateDaily: number | null;
    monthsRemaining: number;
    projectedExhaustionDate: string | null;
    alertEmailSent: boolean;
    reportData: ReportData | null;
}

export interface ReportData {
    reportDate: string;
    program: { serial: string; startDate: string | null; endDate: string | null };
    packs: PackInfo[];
    summary: ReportSummary;
    thresholds: { warningMonths: number; criticalMonths: number };
    forecast: ForecastItem[];
    stats: {
        totalConfigs: number;
        totalEntitlements: number;
        activeEntitlements: number;
        pendingEntitlements: number;
        uniqueAccountIds: number[];
    };
    accounts: AccountBreakdown[];
}

export interface ReportSummary {
    pointsTotal: number;
    pointsUsed: number;
    pointsRemaining: number;
    pctConsumed: number;
    burnRate30d: number;
    burnRateDaily: number | null;
    monthsRemaining: number;
    projectedDays: number;
    projectedExhaustionDate: string | null;
    alertLevel: string;
    // true while Fortinet has not published the day's points yet: every
    // daily figure is pending, not zero.
    dailyDataPending?: boolean;
    lastPublishedDaily?: number | null;
    projectionBasis?: string;
}

export interface PackInfo {
    packNumber: string;
    packIndex: number;
    totalPoints: number;
    startDate: string;
    expirationDate: string;
    status: string;
}

export interface ForecastItem {
    month: number;
    date: string;
    projectedUsed: number;
    projectedRemaining: number;
    pctConsumed: number;
    burnInPeriod: number;
    monthsLeft: number;
    alertLevel: string;
}

export interface AccountBreakdown {
    accountId: number;
    label: string;
    entitlements: EntitlementDetail[];
    subtotal30d: number;
    subtotalCumulative: number;
    subtotalDaily: number | null;
    pctOfTotal: number | null;
    entitlementCount: number;
}

export interface EntitlementDetail {
    serialNumber: string;
    configName: string;
    description: string;
    status: string;
    startDate: string | null;
    endDate: string | null;
    points30d: number;
    pointsCumulative: number;
    pointsDaily: number | null;
}

export interface FortiFlexPack {
    id: number;
    packNumber: string;
    packIndex: number;
    totalPoints: number;
    startDate: string;
    expirationDate: string;
    status: string;
    notes: string | null;
}

export interface FortiFlexAccountLabel {
    id: number;
    accountId: string;
    label: string;
    autoDiscovered: boolean;
}

export interface FortiFlexAlertRecipient {
    id: number;
    email: string;
    name: string | null;
    enabled: boolean;
}

export interface HistoryReport {
    id: number;
    reportDate: string;
    alertLevel: string;
    pointsTotal: number;
    pointsUsed: number;
    pointsRemaining: number;
    burnRate30d: number;
    burnRateDaily: number | null;
    monthsRemaining: number;
    projectedExhaustionDate: string | null;
}

export interface AccountSeriesPoint {
    date: string;
    daily: number | null;
    cumulative: number;
    monthly: number;
    entitlements: number;
}

export interface AccountTimeSeries {
    accountId: number;
    label: string;
    series: AccountSeriesPoint[];
}

export interface DailyTotal {
    date: string;
    totalDaily: number | null;
    pointsUsed: number;
    pointsRemaining: number;
    burnRateDaily: number | null;
    dailyDataPending?: boolean;
}

export interface AccountHistoryData {
    days: number;
    totalReports: number;
    dailyTotals: DailyTotal[];
    accounts: AccountTimeSeries[];
}

export function useFortiFlexData(api: AxiosInstance) {
    const [report, setReport] = useState<FortiFlexReport | null>(null);
    const [history, setHistory] = useState<HistoryReport[]>([]);
    const [accountHistory, setAccountHistory] = useState<AccountHistoryData | null>(null);
    const [packs, setPacks] = useState<FortiFlexPack[]>([]);
    const [accountLabels, setAccountLabels] = useState<FortiFlexAccountLabel[]>([]);
    const [alertRecipients, setAlertRecipients] = useState<FortiFlexAlertRecipient[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

    const refreshPacks = useCallback(async () => {
        try {
            const res = await getPacks(api);
            if (res.data?.success) setPacks(res.data.data || []);
        } catch { /* silent */ }
    }, [api]);

    const refreshLabels = useCallback(async () => {
        try {
            const res = await getAccountLabels(api);
            if (res.data?.success) setAccountLabels(res.data.data || []);
        } catch { /* silent */ }
    }, [api]);

    const refreshRecipients = useCallback(async () => {
        try {
            const res = await getAlertRecipients(api);
            if (res.data?.success) setAlertRecipients(res.data.data || []);
        } catch { /* silent */ }
    }, [api]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const [reportRes, historyRes, acctHistRes, packsRes, labelsRes, recipientsRes] =
                    await Promise.allSettled([
                        getLatestReport(api),
                        getReportHistory(api, 365, 0),
                        getAccountHistory(api, 365),
                        getPacks(api),
                        getAccountLabels(api),
                        getAlertRecipients(api),
                    ]);

                if (cancelled) return;

                if (reportRes.status === 'fulfilled' && reportRes.value.data?.success) {
                    setReport(reportRes.value.data.data);
                }
                if (historyRes.status === 'fulfilled' && historyRes.value.data?.success) {
                    setHistory(historyRes.value.data.data?.reports || []);
                }
                if (acctHistRes.status === 'fulfilled' && acctHistRes.value.data?.success) {
                    setAccountHistory(acctHistRes.value.data.data || null);
                }
                if (packsRes.status === 'fulfilled' && packsRes.value.data?.success) {
                    setPacks(packsRes.value.data.data || []);
                }
                if (labelsRes.status === 'fulfilled' && labelsRes.value.data?.success) {
                    setAccountLabels(labelsRes.value.data.data || []);
                }
                if (recipientsRes.status === 'fulfilled' && recipientsRes.value.data?.success) {
                    setAlertRecipients(recipientsRes.value.data.data || []);
                }
            } catch (e: unknown) {
                if (!cancelled) {
                    const msg =
                        e instanceof Error ? e.message : 'Failed to load FortiFlex data';
                    setError(msg);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [api, refreshKey]);

    return {
        report,
        history,
        accountHistory,
        packs,
        accountLabels,
        alertRecipients,
        loading,
        error,
        refresh,
        refreshPacks,
        refreshLabels,
        refreshRecipients,
        setPacks,
        setAccountLabels,
        setAlertRecipients,
    };
}
